import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import AccountIcon from '../Components/AccountIcon';
import ConsentSettings from '../Components/ConsentSettings';
import Modal from '../Components/Modal';
import { useSelectedCompany } from '../Commoncontext/SelectedCompanyContext';
import { useUsers } from '../Commoncontext/UsersContext';
import UsersApi from '../Api/UsersApi';
import CompanyUtils from '../Utils/CompanyUtils';
import styles from '../Styles/Owninformation.module.css';

const ALL_ROLES = ['asukas', 'osakas', 'hallitus', 'isännöitsijä'];

function Owninformation() {
  const { selectedCompanyId } = useSelectedCompany();
  const { user, selectedUser, fetchUserById, setUser, fetchUsers } = useUsers();
  const { id: routeUserId } = useParams();

  const isOwnProfile = !routeUserId || routeUserId === user?.uid;
  const currentUserId = isOwnProfile ? user?.uid : routeUserId;
  const editingUser = isOwnProfile ? user : selectedUser;

  const [formDetails, setFormDetails] = useState(editingUser || {});
  const [originalDetails, setOriginalDetails] = useState(editingUser || {});
  const [isModified, setIsModified] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const [modalMessage, setModalMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPwd: '',
    newPwd: '',
    confirmPwd: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const shouldHideImage = !formDetails.uid || formDetails.uid !== currentUserId;

  const isPrivilegedUser = useMemo(() => {
    return user?.userRoles?.some(
      (r) =>
        r.companyId === selectedCompanyId &&
        (r.role === 'hallitus' || r.role === 'isännöitsijä' || r.role === 'admin')
    );
  }, [user, selectedCompanyId]);

  useEffect(() => {
    if (!isOwnProfile && routeUserId && selectedCompanyId) {
      fetchUserById(routeUserId, selectedCompanyId);
    }
  }, [isOwnProfile, routeUserId, selectedCompanyId, fetchUserById]);

  useEffect(() => {
    if (isOwnProfile && user) {
      setFormDetails(user);
      setOriginalDetails(user);
      setIsModified(false);
      setErrors({});
    } else if (!isOwnProfile && selectedUser) {
      setFormDetails(selectedUser);
      setOriginalDetails(selectedUser);
      setIsModified(false);
      setErrors({});
    }
  }, [isOwnProfile, user, selectedUser]);

  useEffect(() => {
    if (!isPasswordModalOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !changingPassword) {
        closePasswordModal();
      }
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isPasswordModalOpen, changingPassword]);

  const getCompanyRoleNames = (roles) => {
    return (roles || [])
      .filter((r) => r.companyId === selectedCompanyId)
      .map((r) => r.role)
      .sort();
  };

  const rolesAreEqual = (r1, r2) => {
    return JSON.stringify(getCompanyRoleNames(r1)) === JSON.stringify(getCompanyRoleNames(r2));
  };

  const computeIsModified = (updatedDetails, baseDetails) => {
    const profileChanged =
      JSON.stringify({
        etunimi: updatedDetails.etunimi || '',
        sukunimi: updatedDetails.sukunimi || '',
        osoite: updatedDetails.osoite || '',
        postinumero: updatedDetails.postinumero || '',
        postitoimipaikka: updatedDetails.postitoimipaikka || '',
        puhelin: updatedDetails.puhelin || '',
        sähköposti: updatedDetails.sähköposti || '',
        profiilikuvaurl: updatedDetails.profiilikuvaurl || '',
      }) !==
      JSON.stringify({
        etunimi: baseDetails.etunimi || '',
        sukunimi: baseDetails.sukunimi || '',
        osoite: baseDetails.osoite || '',
        postinumero: baseDetails.postinumero || '',
        postitoimipaikka: baseDetails.postitoimipaikka || '',
        puhelin: baseDetails.puhelin || '',
        sähköposti: baseDetails.sähköposti || '',
        profiilikuvaurl: baseDetails.profiilikuvaurl || '',
      });
    const rolesChanged = !rolesAreEqual(updatedDetails.userRoles, baseDetails.userRoles);
    return profileChanged || rolesChanged;
  };

  const getUserRoles = () => {
    const currentRoles = getCompanyRoleNames(formDetails?.userRoles);
    return ALL_ROLES.map((role) => ({
      role,
      checked: currentRoles.includes(role),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formDetails, [name]: value };
    setFormDetails(updated);
    setIsModified(computeIsModified(updated, originalDetails));
  };

  const handleRoleChange = (role, checked) => {
    setFormDetails((prevDetails) => {
      const currentRoles = prevDetails.userRoles || [];
      const updatedRoles = checked
        ? [
            ...currentRoles.filter(
              (r) => !(r.companyId === selectedCompanyId && r.role === role)
            ),
            { companyId: selectedCompanyId, role },
          ]
        : currentRoles.filter(
            (r) => !(r.companyId === selectedCompanyId && r.role === role)
          );
      const updated = { ...prevDetails, userRoles: updatedRoles };
      setIsModified(computeIsModified(updated, originalDetails));
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const { isValid, newErrors, errorMessages } = CompanyUtils.validateUserForm(formDetails);
    setErrors(newErrors);
    if (!isValid) {
      setModalMessage(errorMessages);
      setIsModalOpen(true);
      return;
    }
    const rolesForSelectedCompany = getCompanyRoleNames(formDetails.userRoles);
    if (rolesForSelectedCompany.length === 0) {
      setModalMessage('Valitussa taloyhtiössä on oltava vähintään yksi rooli.');
      setIsModalOpen(true);
      return;
    }
    const rolesChanged = !rolesAreEqual(formDetails.userRoles, originalDetails.userRoles);
    try {
      const profilePayload = {
        etunimi: formDetails.etunimi,
        sukunimi: formDetails.sukunimi,
        osoite: formDetails.osoite,
        postinumero: formDetails.postinumero,
        postitoimipaikka: formDetails.postitoimipaikka,
        puhelin: formDetails.puhelin,
        sähköposti: formDetails.sähköposti,
      };
      await UsersApi.updateUser(formDetails.uid, profilePayload);
      if (rolesChanged) {
        await UsersApi.updateUserRoles(
          formDetails.uid,
          selectedCompanyId,
          rolesForSelectedCompany
        );
      }
      const refreshedDetails = {
        ...formDetails,
        ...profilePayload,
      };
      if (isOwnProfile) {
        setUser(refreshedDetails);
      }
      setFormDetails(refreshedDetails);
      setOriginalDetails(refreshedDetails);
      setIsModified(false);
      setModalMessage('Tiedot päivitetty!');
      if (selectedCompanyId) {
        await fetchUsers(selectedCompanyId);
      }
    } catch (error) {
      setModalMessage('Tietojen päivitys epäonnistui!');
    }
    setIsModalOpen(true);
  };

  const handleReset = () => {
    setFormDetails(originalDetails);
    setErrors({});
    setIsModified(false);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setModalMessage('Sallitut tyypit: JPG, PNG ja GIF.');
      setIsModalOpen(true);
      return;
    }
    if (file.size > 5242880) {
      setModalMessage('Kuvan koko ei saa ylittää 5MB.');
      setIsModalOpen(true);
      return;
    }
    setUploading(true);
    try {
      const imageUrl = await UsersApi.uploadProfileImage(formDetails.uid, file);
      await UsersApi.updateProfileImageUrl(formDetails.uid, imageUrl);
      const updated = { ...formDetails, profiilikuvaurl: imageUrl };
      setFormDetails(updated);
      if (isOwnProfile) {
        setUser(updated);
      }
      setIsModified(computeIsModified(updated, originalDetails));
    } catch (error) {
      setModalMessage('Kuvan lataus epäonnistui!');
      setIsModalOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalMessage('');
  };

  const openPasswordModal = () => {
    setPasswordForm({
      oldPwd: '',
      newPwd: '',
      confirmPwd: '',
    });
    setPasswordError('');
    setPasswordSuccess(false);
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    if (changingPassword) return;
    setIsPasswordModalOpen(false);
    setPasswordForm({
      oldPwd: '',
      newPwd: '',
      confirmPwd: '',
    });
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordOverlayClick = (e) => {
    if (e.target === e.currentTarget && !changingPassword) {
      closePasswordModal();
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    const { oldPwd, newPwd, confirmPwd } = passwordForm;

    if (!auth.currentUser || !auth.currentUser.email) {
      setPasswordError('Käyttäjää ei löytynyt.');
      return;
    }

    if (!oldPwd || !newPwd || !confirmPwd) {
      setPasswordError('Täytä kaikki kentät.');
      return;
    }

    if (newPwd !== confirmPwd) {
      setPasswordError('Uudet salasanat eivät täsmää.');
      return;
    }

    if (newPwd.length < 6) {
      setPasswordError('Uusi salasana on liian heikko (vähintään 6 merkkiä).');
      return;
    }

    setChangingPassword(true);

    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, oldPwd);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPwd);

      setPasswordSuccess(true);

      setTimeout(async () => {
        await auth.signOut();
        window.location.assign('/login');
      }, 1200);
    } catch (err) {
      switch (err.code) {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setPasswordError('Vanha salasana on väärin.');
          break;
        case 'auth/weak-password':
          setPasswordError('Uusi salasana on liian heikko (vähintään 6 merkkiä).');
          break;
        case 'auth/too-many-requests':
          setPasswordError('Liian monta yritystä. Yritä hetken kuluttua uudelleen.');
          break;
        case 'auth/network-request-failed':
          setPasswordError('Verkkovirhe. Tarkista yhteys ja yritä uudelleen.');
          break;
        default:
          setPasswordError('Salasanan vaihto epäonnistui.');
          break;
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const renderPasswordModal = () => {
    if (!isPasswordModalOpen) return null;

    return (
      <>
        <div className={styles.modalBackdrop} onClick={handlePasswordOverlayClick} />
        <div
          className={styles.modalRoot}
          role="dialog"
          aria-modal="true"
          onClick={handlePasswordOverlayClick}
        >
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.modalClose}
              onClick={closePasswordModal}
              disabled={changingPassword}
              type="button"
            >
              ×
            </button>

            {!passwordSuccess ? (
              <>
                <h3 className={styles.modalTitleLarge}>Vaihda salasana</h3>

                <form onSubmit={handleChangePassword}>
                  <div className={styles.formRow}>
                    <label className={styles.label} htmlFor="oldPwd">
                      Vanha salasana
                    </label>
                    <input
                      id="oldPwd"
                      name="oldPwd"
                      type="password"
                      className={styles.input}
                      value={passwordForm.oldPwd}
                      onChange={handlePasswordInputChange}
                      disabled={changingPassword}
                      autoComplete="current-password"
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.label} htmlFor="newPwd">
                      Uusi salasana
                    </label>
                    <input
                      id="newPwd"
                      name="newPwd"
                      type="password"
                      className={styles.input}
                      value={passwordForm.newPwd}
                      onChange={handlePasswordInputChange}
                      disabled={changingPassword}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.label} htmlFor="confirmPwd">
                      Toista uusi salasana
                    </label>
                    <input
                      id="confirmPwd"
                      name="confirmPwd"
                      type="password"
                      className={styles.input}
                      value={passwordForm.confirmPwd}
                      onChange={handlePasswordInputChange}
                      disabled={changingPassword}
                      autoComplete="new-password"
                    />
                  </div>

                  {passwordError && (
                    <div className={styles.passwordError}>{passwordError}</div>
                  )}

                  <div>
                    <button
                      type="submit"
                      className={styles.primaryBtn}
                      disabled={changingPassword}
                    >
                      {changingPassword ? 'Tallennetaan...' : 'Vaihda salasana'}
                    </button>

                    <button
                      type="button"
                      className={styles.cancelBtnTight}
                      onClick={closePasswordModal}
                      disabled={changingPassword}
                    >
                      Peruuta
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h3 className={styles.modalTitleLarge}>Salasana vaihdettu onnistuneesti</h3>
                <div className={styles.mutedSmall2}>
                  Odota hetki, sinut ohjataan sisäänkirjautumissivulle.
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <main className={styles.content}>
      <div className={styles.pageShell}>
        <div className={styles.pageGrid}>
          <section className={styles.card}>
            <h2>{isOwnProfile ? 'Omat perustiedot' : 'Käyttäjän perustiedot'}</h2>

            <div className={styles.profileContainer}>
              <AccountIcon
                key={formDetails.uid || 'no-image'}
                profileImageUrl={shouldHideImage ? null : formDetails.profiilikuvaurl}
                width="96px"
                height="96px"
                border={true}
              />
              <label
                htmlFor="file-input"
                className={styles.secondaryButton}
                style={{ display: uploading ? 'none' : 'inline-flex' }}
              >
                {formDetails.profiilikuvaurl ? 'Vaihda profiilikuva' : 'Lisää profiilikuva'}
              </label>
              <input
                id="file-input"
                type="file"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              {uploading && <p className={styles.uploadText}>Ladataan...</p>}
            </div>

            <form onSubmit={handleSubmit}>
              <div className={styles.row}>
                <div className={styles.inputGroup}>
                  <label htmlFor="etunimi">Etunimi</label>
                  <input
                    id="etunimi"
                    name="etunimi"
                    type="text"
                    className={styles.input}
                    value={formDetails.etunimi || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor="sukunimi">Sukunimi</label>
                  <input
                    id="sukunimi"
                    name="sukunimi"
                    type="text"
                    className={styles.input}
                    value={formDetails.sukunimi || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.inputGroupFull}>
                  <label htmlFor="osoite">Osoite</label>
                  <input
                    id="osoite"
                    name="osoite"
                    type="text"
                    className={styles.input}
                    value={formDetails.osoite || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.inputGroup}>
                  <label htmlFor="postinumero">Postinumero</label>
                  <input
                    id="postinumero"
                    name="postinumero"
                    type="text"
                    className={styles.input}
                    value={formDetails.postinumero || ''}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="postitoimipaikka">Postitoimipaikka</label>
                  <input
                    id="postitoimipaikka"
                    name="postitoimipaikka"
                    type="text"
                    className={styles.input}
                    value={formDetails.postitoimipaikka || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.inputGroup}>
                  <label htmlFor="puhelin">Puhelin</label>
                  <input
                    id="puhelin"
                    name="puhelin"
                    type="text"
                    className={styles.input}
                    value={formDetails.puhelin || ''}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="sähköposti">Sähköposti</label>
                  <input
                    id="sähköposti"
                    name="sähköposti"
                    type="text"
                    value={formDetails.sähköposti || ''}
                    disabled={true}
                    className={styles.disabledInput}
                    title="Sähköposti toimii käyttäjätunnuksenasi palveluun"
                  />
                </div>
              </div>
            </form>
          </section>

          <section className={`${styles.card} ${styles.rightColumnCard}`}>
            <h2>Suostumus ja yhteydenpito</h2>
            <div className={styles.consentWrapper}>
              <ConsentSettings />
            </div>
          </section>

          <section className={styles.card}>
            <h2>Roolit ja käyttöoikeudet</h2>
            <div className={styles.rolesRow}>
              {getUserRoles().map(({ role, checked }) => (
                <label key={role} className={styles.roleItem}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!isPrivilegedUser}
                    onChange={
                      isPrivilegedUser ? (e) => handleRoleChange(role, e.target.checked) : undefined
                    }
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>
          </section>

          <section className={`${styles.card} ${styles.securityCard}`}>
            <h2>Turvallisuus</h2>
            <div className={styles.securityActions}>
              <button
                type="button"
                className={styles.securityButton}
                onClick={openPasswordModal}
              >
                <span className={styles.securityButtonLeft}>
                  <span className={styles.securityIcon}>🔒</span>
                  <span>Vaihda salasana</span>
                </span>
                <span className={styles.securityChevron}>›</span>
              </button>

              <button
                type="button"
                className={styles.securityButton}
                disabled
                title="Tulossa myöhemmin"
              >
                <span className={styles.securityButtonLeft}>
                  <span className={styles.securityIcon}>✉️</span>
                  <span>Vaihda sähköposti</span>
                </span>
                <span className={styles.securityChevron}>›</span>
              </button>
            </div>
          </section>
        </div>

        <div className={styles.buttonContainer}>
          <button
            className={`${styles.saveButton} ${!isModified ? styles.disabledButton : ''}`}
            onClick={handleSubmit}
            type="button"
            disabled={!isModified}
          >
            Tallenna muutokset
          </button>
          <button
            className={`${styles.secondaryButton} ${!isModified ? styles.disabledButton : ''}`}
            onClick={handleReset}
            type="button"
            disabled={!isModified}
          >
            Peruuta muutokset
          </button>
        </div>
      </div>

      {isModalOpen && (
        <Modal onClose={closeModal} showCancelButton={false} size="small">
          {Array.isArray(modalMessage) ? (
            <div>
              <p>Korjaa seuraavat virheet:</p>
              <ul className={styles.errorList}>
                {modalMessage.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p>{modalMessage}</p>
          )}
        </Modal>
      )}

      {renderPasswordModal()}
    </main>
  );
}

export default Owninformation;